import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { SVGProps } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Facebook, Instagram, Linkedin, Youtube, Twitter, MessageCircle, Globe, Github, Twitch, Send } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { normalizeExternalUrl, openExternalLink } from "@/lib/openExternalLink";

// Custom TikTok icon (Lucide doesn't ship an accurate one)
type TikTokIconProps = SVGProps<SVGSVGElement> & { size?: number; color?: string; strokeWidth?: number };

const TikTokIcon: LucideIcon = (({ size = 24, color = "currentColor", strokeWidth = 0, className, ...props }: TikTokIconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    stroke="none"
    strokeWidth={strokeWidth}
    className={className}
    {...props}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.65a8.16 8.16 0 0 0 4.77 1.52V6.72a4.85 4.85 0 0 1-1.84-.03Z" />
  </svg>
)) as unknown as LucideIcon;

export const SOCIAL_ICON_MAP: Record<string, LucideIcon> = {
  instagram: Instagram,
  facebook: Facebook,
  linkedin: Linkedin,
  youtube: Youtube,
  x: Twitter,
  twitter: Twitter,
  tiktok: TikTokIcon,
  whatsapp: MessageCircle,
  telegram: Send,
  github: Github,
  twitch: Twitch,
  website: Globe,
};

export const SOCIAL_ICON_KEYS = Object.keys(SOCIAL_ICON_MAP);

interface SocialNetworkRow {
  id: string;
  name: string;
  url: string;
  url_es: string | null;
  icon_key: string;
  color: string | null;
  color_footer: string | null;
  display_order: number;
}

interface SocialIconsProps {
  className?: string;
  size?: number;
  iconClassName?: string;
  variant?: "header" | "footer";
}

const SocialIcons = ({ className, size = 18, iconClassName, variant = "header" }: SocialIconsProps) => {
  const { i18n } = useTranslation();
  const isEs = i18n.language?.startsWith("es");
  const { data } = useQuery({
    queryKey: ["social_networks", "public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("social_networks")
        .select("id,name,url,url_es,icon_key,color,color_footer,display_order")
        .eq("is_enabled", true)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as SocialNetworkRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  if (!data || data.length === 0) return null;

  return (
    <TooltipProvider delayDuration={150}>
      <div className={cn("flex items-center", variant === "header" ? "gap-1" : "gap-2", className)}>
        {data.map((row) => {
          const Icon = SOCIAL_ICON_MAP[row.icon_key] ?? Globe;
          const appliedColor = variant === "footer" ? (row.color_footer ?? row.color) : row.color;
          const url = normalizeExternalUrl(isEs && row.url_es ? row.url_es : row.url);
          return (
            <Tooltip key={row.id}>
              <TooltipTrigger asChild>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer external"
                  referrerPolicy="no-referrer"
                  onClick={(event) => {
                    event.preventDefault();
                    openExternalLink(url);
                  }}
                  aria-label={row.name}
                  style={appliedColor ? { color: appliedColor } : undefined}
                  className={cn(
                    "inline-flex h-9 w-9 items-center justify-center rounded-md bg-transparent transition-opacity hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-current/40",
                    iconClassName
                  )}
                >
                  <Icon size={size} strokeWidth={1.75} />
                </a>
              </TooltipTrigger>
              <TooltipContent side="top">{row.name}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
};

export default SocialIcons;
